function createId(prefix = "inst") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export const ALLOWED_CHILDREN = {
  project: ["module", "element", "hardware"],
  module: ["module", "element", "hardware"],
  element: ["element", "hardware"],
  hardware: [],
};

export function canAddChild(parentNodeType, childNodeType) {
  const parent = String(parentNodeType || "element");
  const child = String(childNodeType || "element");
  return (ALLOWED_CHILDREN[parent] || []).includes(child);
}

function isBoardDefinition(definition) {
  return (
    definition?.nodeType === "element" &&
    String(definition?.category || "").startsWith("board.")
  );
}

function getBoardRole(definition) {
  const category = String(definition?.category || "").toLowerCase();

  if (category.includes("side")) return "side";
  if (category.includes("top")) return "top";
  if (category.includes("bottom")) return "bottom";
  if (category.includes("shelf")) return "shelf";
  if (category.includes("back")) return "back";

  return "generic";
}

function createDefaultBindings(definition) {
  if (!isBoardDefinition(definition)) return {};

  const role = getBoardRole(definition);

  if (role === "side") {
    return {
      width: "parent_boardThickness",
      height: "parent_height",
      depth: "parent_depth",
    };
  }

  if (role === "top" || role === "bottom" || role === "shelf") {
    return {
      width: "parent_width",
      height: "parent_boardThickness",
      depth: "parent_depth",
    };
  }

  if (role === "back") {
    return {
      width: "parent_width",
      height: "parent_height",
      depth: "parent_backThickness",
    };
  }

  return {
    width: "parent_width",
    height: "parent_height",
    depth: "parent_boardThickness",
  };
}

function createDefaultTransform() {
  return {
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
  };
}

function createTransformForCabinetRole(role) {
  if (role === "left") {
    return {
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
    };
  }

  if (role === "right") {
    return {
      position: {
        x: "parent_width - parent_boardThickness",
        y: 0,
        z: 0,
      },
      rotation: { x: 0, y: 0, z: 0 },
    };
  }

  if (role === "bottom") {
    return {
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
    };
  }

  if (role === "top") {
    return {
      position: {
        x: 0,
        y: "parent_height - parent_boardThickness",
        z: 0,
      },
      rotation: { x: 0, y: 0, z: 0 },
    };
  }

  if (role === "back") {
    return {
      position: {
        x: 0,
        y: 0,
        z: "parent_depth - parent_backThickness",
      },
      rotation: { x: 0, y: 0, z: 0 },
    };
  }

  return createDefaultTransform();
}

function createBindingsForCabinetRole(role) {
  if (role === "left" || role === "right") {
    return {
      width: "parent_boardThickness",
      height: "parent_height",
      depth: "parent_depth",
    };
  }

  if (role === "top" || role === "bottom") {
    return {
      width: "parent_width",
      height: "parent_boardThickness",
      depth: "parent_depth",
    };
  }

  if (role === "back") {
    return {
      width: "parent_width",
      height: "parent_height",
      depth: "parent_backThickness",
    };
  }

  return {};
}

function cloneInstanceChildrenWithNewIds(children = []) {
  return (children || []).map((child) => ({
    ...child,
    id: createId("inst_child"),
    children: cloneInstanceChildrenWithNewIds(child.children || []),
  }));
}

export function createChildInstanceFromDefinition(definition) {
  return {
    id: createId("inst"),
    definitionId: definition.id,
    name: definition.name || definition.code || "New child",
    role: null,
    transform: createDefaultTransform(),
    parameterBindings: createDefaultBindings(definition),
    parameterOverrides: {},
    children: cloneInstanceChildrenWithNewIds(definition.children || []),
  };
}

function findDefinitionByCategory(definitions = [], category) {
  return (definitions || []).find(
    (def) => String(def.category || "") === String(category)
  );
}

function findBoardDefinition(definitions = [], role) {
  if (role === "left" || role === "right") {
    return (
      findDefinitionByCategory(definitions, "board.side") ||
      findDefinitionByCategory(definitions, "board.panel")
    );
  }

  if (role === "top") {
    return (
      findDefinitionByCategory(definitions, "board.top") ||
      findDefinitionByCategory(definitions, "board.horizontal") ||
      findDefinitionByCategory(definitions, "board.panel")
    );
  }

  if (role === "bottom") {
    return (
      findDefinitionByCategory(definitions, "board.bottom") ||
      findDefinitionByCategory(definitions, "board.horizontal") ||
      findDefinitionByCategory(definitions, "board.panel")
    );
  }

  if (role === "back") {
    return (
      findDefinitionByCategory(definitions, "board.back") ||
      findDefinitionByCategory(definitions, "board.panel")
    );
  }

  return null;
}

export function createCabinetChildren(definitions = []) {
  const roles = ["left", "right", "bottom", "top", "back"];

  return roles
    .map((role) => {
      const def = findBoardDefinition(definitions, role);
      if (!def) return null;

      return {
        id: createId(`cab_${role}`),
        definitionId: def.id,
        name: `${role} - ${def.name || def.code}`,
        role,
        transform: createTransformForCabinetRole(role),
        parameterBindings: createBindingsForCabinetRole(role),
        parameterOverrides: {},
        children: [],
      };
    })
    .filter(Boolean);
}

export function createCabinetInstance(definition, allDefinitions = []) {
  return {
    id: createId("cabinet"),
    definitionId: definition.id,
    name: definition.name || definition.code || "Cabinet",
    role: "cabinet",
    transform: createDefaultTransform(),
    parameterBindings: {},
    parameterOverrides: {
      width: 600,
      height: 720,
      depth: "defaultCabinetDepth",
      boardThickness: "boardThickness",
      backThickness: "backThickness",
    },
    children: createCabinetChildren(allDefinitions),
  };
}

export function shouldAutoBuildCabinet(definition) {
  return (
    definition?.nodeType === "module" &&
    String(definition?.category || "").startsWith("cabinet.")
  );
}

function patchNodeChildren(node, targetId, childInstance) {
  if (!node) return node;

  if (String(node.id) === String(targetId)) {
    return {
      ...node,
      children: [...(node.children || []), childInstance],
    };
  }

  return {
    ...node,
    children: (node.children || []).map((child) =>
      patchNodeChildren(child, targetId, childInstance)
    ),
  };
}

export function addChildToInstanceTree(rootInstances = [], targetId, childInstance) {
  return (rootInstances || []).map((root) =>
    patchNodeChildren(root, targetId, childInstance)
  );
}